import React, { FC, useEffect, useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import AnalyticsUniqueUsers from '../../components/AnalyticsUniqueUsers';
import AnalyticsUsersByRole from '../../components/AnalyticsUsersByRole';
import AnalyticsCreatedProjects from '../../components/AnalyticsCreatedProjects';
import AnalyticsUpdatedProjects from '../../components/AnalyticsUpdatedProjects';
import AnalyticsTimeComparison from '../../components/AnalyticsTimeComparison';
import AnalyticsPagesVisited from '../../components/AnalyticsPagesVisited';
import Text from '@ingka/text';
import { api as AnalyticsApi } from '../../store/utils/analyticsApi';
import AnalyticsPagesTimeSpent from '../../components/AnalyticsPagesTimeSpent';
import DefaultPageLayout from '../../common/DefaultPageLayout';
import { ANALYTICS_PRECONFIGURED_TIME_RANGES } from '../../components/AnalyticsSidebar/utils';
import { AnalyticsSidebar } from '../../components/AnalyticsSidebar/AnalyticsSidebar';
import AnalyticsUserSessionsCount from '../../components/AnalyticsUserSessionCount';
import AnalyticsUserSessionDuration from '../../components/AnalyticsUserSessionDuration';
import AnalyticsUsersFeedback from '../../components/AnalyticsUsersFeedback';

interface AnalyticsState {
  urlPath: string;
  period: {
    start: Date;
    end: Date;
  };
  data: any;
  loading: boolean;
  error: string | null;
}

const initialState = (urlPath: string): AnalyticsState => ({
  urlPath: urlPath,
  period: {
    start: ANALYTICS_PRECONFIGURED_TIME_RANGES[0].getStart(),
    end: undefined,
  },
  data: [],
  loading: false,
  error: null,
});

const fetchData = async (
  state: AnalyticsState,
  setState: React.Dispatch<React.SetStateAction<AnalyticsState>>,
) => {
  setState((prev) => ({ ...prev, loading: true, error: null }));

  try {
    const { data, status } = await AnalyticsApi.get(state.urlPath, {
      params: {
        start: state.period.start.getTime(),
        end: state.period.end?.getTime(),
      },
    });

    if (status !== 200) throw new Error('Failed to fetch data');

    setState((prev) => ({ ...prev, data, loading: false }));
  } catch (err: any) {
    setState((prev) => ({ ...prev, error: err.message, loading: false }));
  }
};

const changePeriod = (
  setState: React.Dispatch<React.SetStateAction<AnalyticsState>>,
  start: Date,
  end: Date = undefined,
) => {
  setState((prev) => ({ ...prev, period: { start: start, end: end } }));
};

const Analytics: FC = () => {
  const [groupingMethod, setGroupingMethod] = useState('day');

  const [userState, setUserState] = useState(initialState('/user-data'));
  const [userFeedbackState, setUserFeedbackState] = useState(
    initialState('/user-feedback-data'),
  );
  const [userSessionsCountState, setUserSessionsCountState] = useState(
    initialState('/user-sessions-count-data'),
  );
  const [userSessionsDurationState, setUserSessionsDurationState] = useState(
    initialState('/user-sessions-avg-duration-data'),
  );
  const [pageViewCountState, setPageViewCountState] = useState(
    initialState('/page-viewed'),
  );
  const [pageRetentionDurationState, setPageRetentionDurationState] = useState(
    initialState('/page-duration'),
  );
  const [projectCreateState, setProjectCreateState] = useState(
    initialState('/projects-create-data'),
  );
  const [projectUpdateState, setProjectUpdateState] = useState(
    initialState('/projects-update-data'),
  );
  const [projectCreateDurationState, setProjectCreateDurationState] = useState(
    initialState('/projects-time-creation-data'),
  );
  const [projectUpdateDurationState, setProjectUpdateDurationState] = useState(
    initialState('/projects-time-update-data'),
  );

  useEffect(() => {
    fetchData(userState, setUserState);
  }, [userState.period]);

  useEffect(() => {
    fetchData(userFeedbackState, setUserFeedbackState);
  }, [userFeedbackState.period]);

  useEffect(() => {
    fetchData(userSessionsCountState, setUserSessionsCountState);
  }, [userSessionsCountState.period]);

  useEffect(() => {
    fetchData(userSessionsDurationState, setUserSessionsDurationState);
  }, [userSessionsDurationState.period]);

  useEffect(() => {
    fetchData(pageViewCountState, setPageViewCountState);
  }, [pageViewCountState.period]);

  useEffect(() => {
    fetchData(pageRetentionDurationState, setPageRetentionDurationState);
  }, [pageRetentionDurationState.period]);

  useEffect(() => {
    fetchData(projectCreateState, setProjectCreateState);
  }, [projectCreateState.period]);

  useEffect(() => {
    fetchData(projectUpdateState, setProjectUpdateState);
  }, [projectUpdateState.period]);

  useEffect(() => {
    fetchData(projectCreateDurationState, setProjectCreateDurationState);
  }, [projectCreateDurationState.period]);

  useEffect(() => {
    fetchData(projectUpdateDurationState, setProjectUpdateDurationState);
  }, [projectUpdateDurationState.period]);

  const allStates = [
    userState,
    pageViewCountState,
    pageRetentionDurationState,
    projectCreateState,
    projectUpdateState,
    projectCreateDurationState,
    projectUpdateDurationState,
  ];

  const isLoading = useMemo(
    () => allStates.some((s) => s.loading),
    [allStates],
  );

  const changePeriods = (start: Date, end: Date = undefined) => {
    changePeriod(setUserState, start, end);
    changePeriod(setUserFeedbackState, start, end);
    changePeriod(setUserSessionsDurationState, start, end);
    changePeriod(setUserSessionsCountState, start, end);
    changePeriod(setPageViewCountState, start, end);
    changePeriod(setPageRetentionDurationState, start, end);
    changePeriod(setProjectCreateState, start, end);
    changePeriod(setProjectUpdateState, start, end);
    changePeriod(setProjectCreateDurationState, start, end);
    changePeriod(setProjectUpdateDurationState, start, end);
  };

  return (
    <>
      <DefaultPageLayout
        sidebar={() => (
          <AnalyticsSidebar
            isLoading={isLoading}
            handleDatePeriodChange={changePeriods}
            handleGroupingMethodChange={setGroupingMethod}
            groupingMethod={groupingMethod}
          />
        )}
        mainPart={() => (
          <Stack
            alignItems={'center'}
            justifyContent={'center'}
            spacing={2}
            p={4}
          >
            <Text textStyle="Heading.L" tagName="h4">
              Co-Worker Satisfaction
            </Text>
            <Stack
              flexDirection={'row'}
              gap={2}
              p={3}
              width={'100%'}
              height={'500px'}
            >
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Monthly Score
                </Text>
                <AnalyticsUsersFeedback
                  data={userFeedbackState.data || []}
                  loading={userFeedbackState.loading}
                />
              </Stack>
            </Stack>

            <Text textStyle="Heading.L" tagName="h4">
              Active Users
            </Text>
            <Stack
              flexDirection={'row'}
              gap={2}
              p={3}
              width={'100%'}
              height={'500px'}
            >
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Active Users
                </Text>
                <AnalyticsUniqueUsers
                  data={userState.data || []}
                  grouping={groupingMethod}
                  loading={userState.loading}
                />
              </Stack>
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Users by Role
                </Text>
                <AnalyticsUsersByRole
                  data={userState.data || []}
                  grouping={groupingMethod}
                  loading={userState.loading}
                />
              </Stack>
            </Stack>

            <Text textStyle="Heading.L" tagName="h4">
              User Sessions
            </Text>
            <Stack
              flexDirection={'row'}
              gap={2}
              p={3}
              width={'100%'}
              height={'500px'}
            >
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Quantity
                </Text>
                <AnalyticsUserSessionsCount
                  data={userSessionsCountState.data || []}
                  grouping={groupingMethod}
                  loading={userSessionsCountState.loading}
                />
              </Stack>
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Average Duration
                </Text>
                <AnalyticsUserSessionDuration
                  data={userSessionsDurationState.data || []}
                  grouping={groupingMethod}
                  loading={userSessionsDurationState.loading}
                />
              </Stack>
            </Stack>

            <Text textStyle="Heading.L" tagName="h4">
              Pages Statistics
            </Text>
            <Stack
              flexDirection={'row'}
              gap={2}
              p={3}
              width={'100%'}
              height={'500px'}
            >
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Visit Times
                </Text>
                <AnalyticsPagesVisited
                  data={pageViewCountState.data || []}
                  loading={pageViewCountState.loading}
                />
              </Stack>
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Retention Duration
                </Text>
                <AnalyticsPagesTimeSpent
                  data={pageRetentionDurationState.data || []}
                  loading={pageRetentionDurationState.loading}
                />
              </Stack>
            </Stack>

            <Text textStyle="Heading.L" tagName="h4">
              Project Actions
            </Text>
            <Stack
              flexDirection={'row'}
              gap={2}
              p={3}
              width={'100%'}
              height={'500px'}
            >
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Projects Created
                </Text>
                <AnalyticsCreatedProjects
                  data={projectCreateState.data || []}
                  grouping={groupingMethod}
                  loading={projectCreateState.loading}
                />
              </Stack>
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Projects Updated
                </Text>
                <AnalyticsUpdatedProjects
                  data={projectUpdateState.data || []}
                  grouping={groupingMethod}
                  loading={projectUpdateState.loading}
                />
              </Stack>
            </Stack>

            <Text textStyle="Heading.L" tagName="h4">
              Average Time spent on project actions
            </Text>
            <Stack
              flexDirection={'row'}
              gap={2}
              p={3}
              width={'100%'}
              height={'500px'}
            >
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Project Create
                </Text>
                <AnalyticsTimeComparison
                  data={projectCreateDurationState.data}
                  loading={projectCreateDurationState.loading}
                />
              </Stack>
              <Stack flex={1} p={3} border={'1px solid black'}>
                <Text textStyle="Heading.S" tagName="h4">
                  Project Update
                </Text>
                <AnalyticsTimeComparison
                  data={projectUpdateDurationState.data}
                  loading={projectUpdateDurationState.loading}
                />
              </Stack>
            </Stack>
          </Stack>
        )}
      />
    </>
  );
};

export default Analytics;
